import {Component, forwardRef, Input, OnInit, OnDestroy, Output, EventEmitter} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {
  DIGITS,
  LOWERCASE_LETTERS,
  SPECIAL_CHARACTERS,
  UPPERCASE_LETTERS
} from "../../constants/global.constants";
import {PROMO_CODE_LENGTH} from "../../constants/promo-code.constant";

@Component({
  selector: 'app-filled-form-field',
  templateUrl: './filled-form-field.component.html',
  styleUrl: './filled-form-field.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilledFormFieldComponent),
      multi: true
    }
  ]
})
export class FilledFormFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input('hideRequiredMarker') hideRequiredMarker: boolean = true;
  @Input('hideSelectIcon') hideSelectIcon: boolean = true;
  @Input('readonly') readonly: boolean = false;
  @Input('width') width: string = '';
  @Input('label') label: string = 'label';
  @Input('type') type: 'input' | 'textarea' = 'input';
  @Input('optional') optional: boolean = false;
  @Input() isControlInvalid?: boolean = false;
  @Input() isOnlyNumber: boolean = false;
  @Input() minNumber: number = 0;
  @Input() maxNumberLength: number = 2;
  @Input() cdkAutosizeMinRows: number = 2;
  @Input() cdkAutosizeMaxRows: number = 2;

  @Input() isSuffixIconVisible: boolean = false;
  @Input() icon: 'arrow' | 'lock' | 'help' | 'cached' | 'percent' | null = null;

  @Input() value: string = '';
  @Input() errorMessage?: string | null;
  @Output() autoGenerated = new EventEmitter<void>();

  iti: any;
  isFocused: boolean = false;

  constructor(public translateService: TranslateService) {}

  private languageSubscription?: Subscription;

  ngOnInit(): void {
    this.languageSubscription = this.translateService.onLangChange.subscribe(() => {
    });
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  writeValue(obj: any): void {
    this.value = String(obj);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
  }

  protected generatePromoCode(): string {
    const promoCode = [
      LOWERCASE_LETTERS[Math.floor(Math.random() * LOWERCASE_LETTERS.length)],
      UPPERCASE_LETTERS[Math.floor(Math.random() * UPPERCASE_LETTERS.length)],
      DIGITS[Math.floor(Math.random() * DIGITS.length)],
      SPECIAL_CHARACTERS[Math.floor(Math.random() * SPECIAL_CHARACTERS.length)],
    ];

    const allCharacters = LOWERCASE_LETTERS + UPPERCASE_LETTERS + DIGITS + SPECIAL_CHARACTERS;
    for (let i = promoCode.length; i < PROMO_CODE_LENGTH; i++) {
      promoCode.push(allCharacters[Math.floor(Math.random() * allCharacters.length)]);
    }

    return promoCode.sort(() => Math.random() - 0.5).join('');
  }

  onKeyPress(event: KeyboardEvent) {
    if (!this.isOnlyNumber) {
      return;
    }
    if(this.value.length >= this.maxNumberLength) {
      event.preventDefault();
      return;
    }
    if (this.value.length == 0 && Number(event.key) < this.minNumber) {
      event.preventDefault();
      this.setValue(String(this.minNumber));
      return;
    }
    const allowedChars = /[^0-9]/g;
    const inputChar = String.fromCharCode(event.charCode);
    if (allowedChars.test(inputChar)) {
      event.preventDefault();
      return;
    }
  }

  protected setValue(value: any) {
    if (this.readonly) return;
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
  }

  private onChange: (value: any) => void = () => {
  };

  private onTouched: () => void = () => {
  };

  protected onGeneratePromoCodeClick(): void {
    const generatedCode = this.generatePromoCode();
    this.setValue(generatedCode);
    this.onChange(generatedCode);
    this.autoGenerated.emit();
  }
}
